import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../components/DashboardLayout';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Trophy, ChevronRight, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PoolTable = ({ pool, teams, forfeitTeamIds }) => (
  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-2 justify-between">
      <div className="flex items-center gap-2">
        <Trophy size={16} className="text-emerald-500" />
        <h3 className="font-heading text-lg font-bold text-black uppercase">Pool {pool}</h3>
      </div>
      <span className="text-[10px] text-gray-600 uppercase tracking-wider">Tiebreaker: GD → GF → GA</span>
    </div>
    <Table>
      <TableHeader>
        <TableRow className="border-gray-200 hover:bg-transparent">
          <TableHead className="text-gray-600 uppercase text-xs font-bold w-12">#</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold">Team</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">P</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center text-emerald-500">W</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center">D</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center text-red-500">L</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center bg-amber-500/8 border-x border-amber-500/10">GF</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center bg-amber-500/5 border-r border-amber-500/10">GA</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center bg-amber-500/10 border-r border-amber-500/10">GD</TableHead>
          <TableHead className="text-gray-600 uppercase text-xs font-bold text-center text-emerald-500">PTS</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {teams.map((team, index) => {
          const gd = team.goals_for - team.goals_against;
          return (
          <TableRow key={team.team_id} className={`border-gray-100 table-row-hover ${index < 2 ? 'bg-emerald-500/5' : ''}`}>
            <TableCell>
              <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                index < 2 ? 'bg-emerald-500 text-black' : 'bg-gray-100 text-gray-500'
              }`}>{index + 1}</span>
            </TableCell>
            <TableCell>
              <div className="flex items-center gap-3">
                {team.logo_url ? (
                  <img src={team.logo_url} alt="" className="w-8 h-8 object-contain" />
                ) : (
                  <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <span className="text-xs font-bold text-gray-600">{team.name.charAt(0)}</span>
                  </div>
                )}
                <span className="text-black font-medium">{team.name}{forfeitTeamIds?.has(team.team_id) ? '*' : ''}</span>
              </div>
            </TableCell>
            <TableCell className="text-center text-gray-700">{team.played}</TableCell>
            <TableCell className="text-center text-emerald-500 font-medium">{team.won}</TableCell>
            <TableCell className="text-center text-gray-600">{team.drawn}</TableCell>
            <TableCell className="text-center text-red-500">{team.lost}</TableCell>
            <TableCell className="text-center text-gray-700 bg-amber-500/[0.03]">{team.goals_for}</TableCell>
            <TableCell className="text-center text-gray-700 bg-amber-500/[0.02]">{team.goals_against}</TableCell>
            <TableCell className={`text-center font-medium bg-amber-500/[0.04] ${
              gd > 0 ? 'text-emerald-500' : gd < 0 ? 'text-red-500' : 'text-gray-600'
            }`}>{gd > 0 ? '+' : ''}{gd}</TableCell>
            <TableCell className="text-center">
              <span className="text-emerald-500 font-bold text-lg">{team.points}</span>
            </TableCell>
          </TableRow>
          );
        })}
      </TableBody>
    </Table>
    <div className="px-4 py-2 border-t border-gray-100 text-xs text-gray-500 flex items-center gap-2">
      <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
      <span>Top 2 qualify for Knockout Stage</span>
    </div>
  </div>
);

const BracketSlot = ({ label, team, isWinner }) => (
  <div className={`px-4 py-2 rounded border text-sm ${
    isWinner ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
    team && team !== 'TBD' ? 'bg-gray-100 border-gray-200 text-black' :
    'bg-gray-50 border-gray-100 text-gray-500'
  }`}>
    <span className="text-[10px] text-gray-500 uppercase block mb-0.5">{label}</span>
    <span className="font-medium">{team || 'TBD'}</span>
  </div>
);

export default function StandingsPage() {
  const { token, organization } = useAuth();
  const { t } = useTranslation();
  const [standingsData, setStandingsData] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (organization) {
      fetchStandings();
      fetchMatches();
    }
  }, [organization]);

  const fetchStandings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/standings/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setStandingsData(await response.json());
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const fetchMatches = async () => {
    try {
      const response = await fetch(`${API_URL}/api/matches/${organization.org_id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) setMatches(await response.json());
    } catch (error) { console.error(error); }
  };

  const hasPools = standingsData?.grouped && standingsData?.pools;
  const pools = hasPools ? standingsData.pools : {};

  // Check if all pool games are done (each team played 2 games)
  const allPoolsDone = hasPools && Object.values(pools).every(poolTeams =>
    poolTeams.every(t => t.played >= 2)
  );

  // Build team lookup from pools data
  const teamLookup = {};
  if (hasPools) {
    Object.values(pools).forEach(poolTeams => {
      poolTeams.forEach(t => { teamLookup[t.team_id] = t.name; });
    });
  }

  // Track forfeit-involved teams
  const forfeitTeamIds = new Set();
  matches.forEach(m => {
    if (m.forfeit) {
      forfeitTeamIds.add(m.home_team_id);
      forfeitTeamIds.add(m.away_team_id);
    }
  });

  // Get knockout match data directly from match documents (populated by backend)
  const knockoutMatches = matches.filter(m => ['quarterfinal', 'semifinal', 'final'].includes(m.stage));
  const qfMatches = knockoutMatches.filter(m => m.stage === 'quarterfinal').sort((a, b) => (a.field || '').localeCompare(b.field || ''));
  const sfMatches = knockoutMatches.filter(m => m.stage === 'semifinal').sort((a, b) => (a.field || '').localeCompare(b.field || ''));
  const finalMatch = knockoutMatches.find(m => m.stage === 'final');

  const getTeamName = (teamId) => {
    if (!teamId || teamId === 'tbd') return 'TBD';
    const name = teamLookup[teamId] || teamId;
    return forfeitTeamIds.has(teamId) ? `${name}*` : name;
  };

  const qfLabels = ['Q1', 'Q2', 'Q3', 'Q4'];
  const qfDescs = ['1st A vs 2nd C', '2nd A vs 1st C', '1st B vs 2nd D', '2nd B vs 1st D'];

  return (
    <DashboardLayout>
      <div data-testid="standings-page" className="space-y-8">
        <div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-black uppercase tracking-tight">
            {t('standings.title')}
          </h1>
          <p className="text-gray-600 mt-2">Current tournament rankings</p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !standingsData || (Array.isArray(standingsData) && standingsData.length === 0) ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <Trophy className="mx-auto text-gray-600 mb-4" size={48} />
            <h3 className="text-black text-lg font-medium mb-2">No standings yet</h3>
            <p className="text-gray-500">Add teams and complete matches to see standings</p>
          </div>
        ) : (
          <>
            {/* Export Button */}
            <div className="flex justify-end">
              <a href={`${API_URL}/api/export/standings/${organization.org_id}`} download className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 rounded-lg text-sm transition-colors" data-testid="export-standings-csv">
                <Download className="w-4 h-4" /> Export Standings CSV
              </a>
            </div>

            {/* Pool Standings */}
            {hasPools ? (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {Object.entries(pools).map(([pool, teams]) => (
                  <PoolTable key={pool} pool={pool} teams={teams} forfeitTeamIds={forfeitTeamIds} />
                ))}
              </div>
            ) : (
              <PoolTable pool="Overall" teams={Array.isArray(standingsData) ? standingsData : standingsData.ungrouped || []} />
            )}

            {/* Knockout Bracket */}
            {hasPools && (
              <div className="mt-10">
                <h2 className="font-heading text-2xl font-bold text-black uppercase mb-6 flex items-center gap-3">
                  <Trophy size={24} className="text-yellow-500" />
                  Knockout Stage
                  {!allPoolsDone && (
                    <span className="text-xs text-gray-500 font-normal normal-case ml-2">(awaiting pool results)</span>
                  )}
                </h2>
                
                <div className="bg-white border border-gray-200 rounded-lg p-6 overflow-x-auto">
                  <div className="flex items-center gap-4 min-w-[900px]">
                    {/* Quarter-Finals */}
                    <div className="flex flex-col gap-6 w-[220px] shrink-0">
                      <h4 className="text-xs text-gray-500 uppercase font-bold text-center mb-1">Quarter-Finals</h4>
                      {qfMatches.map((match, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-1 text-[10px] text-gray-500 uppercase border-b border-gray-100">{qfLabels[i] || `Q${i+1}`}: {qfDescs[i] || ''}</div>
                          <div className="p-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-400 font-bold text-xs w-6">{qfLabels[i]}</span>
                              <div className="flex-1 text-sm text-black px-2 py-1 rounded bg-gray-100">{getTeamName(match.home_team_id)}</div>
                              {match.status === 'completed' && <span className="text-black font-mono text-sm">{match.home_score}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-6" />
                              <div className="flex-1 text-sm text-black px-2 py-1 rounded bg-gray-100">{getTeamName(match.away_team_id)}</div>
                              {match.status === 'completed' && <span className="text-black font-mono text-sm">{match.away_score}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Arrow */}
                    <div className="flex flex-col items-center justify-center shrink-0">
                      <ChevronRight size={24} className="text-emerald-500" />
                    </div>

                    {/* Semi-Finals */}
                    <div className="flex flex-col gap-6 w-[220px] shrink-0 justify-center">
                      <h4 className="text-xs text-gray-500 uppercase font-bold text-center mb-1">Semi-Finals</h4>
                      {sfMatches.map((match, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-1 text-[10px] text-gray-500 uppercase border-b border-gray-100">S{i+1}: Winner Q{i+1} vs Winner Q{i+3}</div>
                          <div className="p-2 space-y-1">
                            <div className="flex items-center gap-2">
                              <span className="text-emerald-400 font-bold text-xs w-6">S{i+1}</span>
                              <div className="flex-1 text-sm text-black px-2 py-1 rounded bg-gray-100">{getTeamName(match.home_team_id)}</div>
                              {match.status === 'completed' && <span className="text-black font-mono text-sm">{match.home_score}</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="w-6" />
                              <div className="flex-1 text-sm text-black px-2 py-1 rounded bg-gray-100">{getTeamName(match.away_team_id)}</div>
                              {match.status === 'completed' && <span className="text-black font-mono text-sm">{match.away_score}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                      {sfMatches.length === 0 && [0, 1].map(i => (
                        <div key={i} className="border border-gray-200 rounded-lg overflow-hidden">
                          <div className="bg-gray-50 px-3 py-1 text-[10px] text-gray-500 uppercase border-b border-gray-100">S{i+1}</div>
                          <div className="p-2 space-y-1">
                            <div className="text-sm text-black px-2 py-1 rounded bg-gray-100">TBD</div>
                            <div className="text-sm text-black px-2 py-1 rounded bg-gray-100">TBD</div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Arrow */}
                    <div className="flex flex-col items-center justify-center shrink-0">
                      <ChevronRight size={24} className="text-emerald-500" />
                    </div>

                    {/* Final */}
                    <div className="flex flex-col gap-6 w-[220px] shrink-0 justify-center">
                      <h4 className="text-xs text-gray-500 uppercase font-bold text-center mb-1">Final</h4>
                      <div className="border border-yellow-500/30 rounded-lg overflow-hidden">
                        <div className="bg-yellow-500/10 px-3 py-1 text-[10px] text-yellow-500 uppercase border-b border-yellow-500/20">Grand Final</div>
                        <div className="p-2 space-y-1">
                          <div className="text-sm text-black px-2 py-1 rounded bg-gray-100">{finalMatch ? getTeamName(finalMatch.home_team_id) : 'TBD'}</div>
                          <div className="text-sm text-black px-2 py-1 rounded bg-gray-100">{finalMatch ? getTeamName(finalMatch.away_team_id) : 'TBD'}</div>
                        </div>
                      </div>
                    </div>

                    {/* Arrow */}
                    <div className="flex flex-col items-center justify-center shrink-0">
                      <ChevronRight size={24} className="text-yellow-500" />
                    </div>

                    {/* Winner */}
                    <div className="flex flex-col gap-6 w-[180px] shrink-0 justify-center">
                      <h4 className="text-xs text-yellow-500 uppercase font-bold text-center mb-1">Champion</h4>
                      <div className="border border-yellow-500/40 rounded-lg p-4 text-center bg-yellow-500/5">
                        <Trophy size={32} className="mx-auto text-yellow-500 mb-2" />
                        <span className="text-yellow-500 font-bold text-lg">
                          {finalMatch && finalMatch.status === 'completed' ? (finalMatch.home_score > finalMatch.away_score ? getTeamName(finalMatch.home_team_id) : getTeamName(finalMatch.away_team_id)) : 'TBD'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex flex-wrap gap-6 text-xs text-gray-500">
          <span>P - Played</span><span>W - Won</span><span>D - Drawn</span><span>L - Lost</span>
          <span>GF - Goals For</span><span>GA - Goals Against</span><span>GD - Goal Difference</span><span>PTS - Points</span>
        </div>
      </div>
    </DashboardLayout>
  );
}
